import { useContext, useEffect, useRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  TitleAndActionBox,
  BillSearchForm,
  PdfPageViewer,
  ScanAndPay,
  isValidBillPhoneNumber,
  isAccountNumberBiller,
  getBillerLabel,
} from "../../common";
import UCashTransactionSuccess from "../../common/UCashTransactionSuccess";
import UCashTransactionReceipt from "../../common/UCashTransactionReceipt";
import DownloadReceipt from "../../common/DownloadReceipt";
import BusinessLoader from "../../common/BusinessLoader";
import ChatwootWidget from "../../common/Chatwoot";
import { ParagraphText, UBButton } from "../../../Libraries";
import { EnterIcon } from "../../../assets/icons/icon";
import { LanguageContext } from "../../../context/LanguageContext";
import { useAuthContext } from "../../../context/AuthContext";
import { useSnackbar } from "../../../context/SnackbarContext";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import { formatNum } from "../../../oscar-pos-core/constants";
import { printWalletReceiptPreview, downloadReceiptImage } from "../../../utils";
import useBillDetails, { PDF_STATUS } from "../../../hooks/useBillDetails";
import useBillDqr from "../../../hooks/useBillDqr";
import { markOpenFreeAccountFlow } from "../../../utils/walletActivationNudge";
import { openChatwootWindow } from "../../../utils/chatwoot";
import {
  buildBillSearchPayload,
  buildBillSearchQueryParams,
  getBillSearchParamsFromQuery,
  hasRequiredBillParams,
} from "./utils";
import formStyles from "../SaleExpense/SaleExpenseForm/styles.module.css";
import billStyles from "../Ucash/BillPayments/BillPayments.module.css";
import styles from "./styles.module.css";

const HIDE_TOGGLE_AMOUNT_THRESHOLD = 20000;
const UCASH_REDIRECT_AMOUNT_THRESHOLD = 100000;

const WebBillPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [I18n] = useContext(LanguageContext);
  const { user, business } = useAuthContext();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    loading,
    error,
    billPdfUrl,
    billDetails,
    pdfStatus,
    billingKey,
    accessToken,
    searchBill,
    resetBill,
  } = useBillDetails();
  // The bill shows as soon as its details arrive; the PDF can still be processing or have failed.
  const hasBill = !!billDetails;
  const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);
  const [hasPdfLoadError, setHasPdfLoadError] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const {
    loading: isGeneratingDqr,
    verifying: isVerifyingPayment,
    dqrDetails,
    isPaymentCompleted,
    paymentReceived,
    totalPayment,
    generateDqr,
    verifyPayment,
    resetDqr,
    continueForRemainingPayment,
  } = useBillDqr();
  const [isScanAndPay, setIsScanAndPay] = useState(false);
  const [isPayingRemaining, setIsPayingRemaining] = useState(false);
  const [remainingAmountToPay, setRemainingAmountToPay] = useState(0);
  const remainingAmount =
    totalPayment != null && paymentReceived != null
      ? Math.max(totalPayment - paymentReceived, 0)
      : 0;
  const [showReceipt, setShowReceipt] = useState(false);
  const [processingTimeSec, setProcessingTimeSec] = useState("");
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const verifyStartedAtRef = useRef(null);
  const receiptRef = useRef();
  const [formValues, setFormValues] = useState(() =>
    getBillSearchParamsFromQuery(searchParams),
  );

  const [requiresAutoSearch] = useState(
    () =>
      hasRequiredBillParams(formValues) &&
      isValidBillPhoneNumber(formValues.phoneValue),
  );
  const [initialPhoneError] = useState(() =>
    formValues.phoneValue && !isValidBillPhoneNumber(formValues.phoneValue)
      ? I18n.phone_number_format
      : "",
  );
  const [hasAttempted, setHasAttempted] = useState(false);

  const runSearch = async (payload) => {
    setHasAttempted(true);
    const success = await searchBill(payload);
    triggerLogEvent(
      analytics,
      success
        ? "web_bill_payment_search_success"
        : "web_bill_payment_search_failed",
      { biller: payload.biller },
    );
  };

  useEffect(() => {
    triggerLogEvent(analytics, "web_bill_payment_view", {
      source: hasRequiredBillParams(formValues) ? "deep_link" : "direct",
    });

    if (requiresAutoSearch) {
      runSearch(buildBillSearchPayload(formValues));
    } else if (hasRequiredBillParams(formValues)) {
      // Params were present but failed validation (e.g. malformed phone_number)
      // before a search was ever attempted.
      triggerLogEvent(analytics, "web_bill_payment_invalid_deep_link_phone", {
        biller: formValues.billerValue,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBillSearch = (values) => {
    setFormValues(values);
    setSearchParams(buildBillSearchQueryParams(values), { replace: true });
    triggerLogEvent(analytics, "web_bill_payment_search_bill", {
      field_type: isAccountNumberBiller(values.billerValue)
        ? "account_number"
        : "reference_number",
      biller: values.billerValue,
    });
    runSearch(buildBillSearchPayload(values));
  };

  const showLoader = loading || (requiresAutoSearch && !hasAttempted);

  useEffect(() => {
    if (hasBill) {
      navigate(`${location.pathname}${location.search}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBill]);

  useEffect(() => {
    if (billPdfUrl) setIsPdfLoading(true);
  }, [billPdfUrl]);

  const handleDownloadBill = async () => {
    if (!billPdfUrl) return;
    triggerLogEvent(analytics, "web_bill_payment_download");
    try {
      const response = await fetch(billPdfUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "bill.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(billPdfUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleBack = () => {
    if (isScanAndPay) {
      triggerLogEvent(analytics, "web_bill_payment_scan_and_pay_back");
      setIsScanAndPay(false);
      setIsPayingRemaining(false);
      resetDqr();
      return;
    }
    triggerLogEvent(analytics, "web_bill_payment_back");
    setIsScanAndPay(false);
    setIsPayingRemaining(false);
    setHasPdfLoadError(false);
    setIsPdfLoading(false);
    resetDqr();
    resetBill();
  };

  const getIsLoggedIn = () =>
    !!(
      user?.phone_number ||
      user?.id ||
      localStorage.getItem("phone_number") ||
      localStorage.getItem("token")
    );

  const handlePayNow = async () => {
    triggerLogEvent(analytics, "web_bill_payment_pay_now", {
      biller: formValues.billerValue,
      amount: displayAmount,
    });

    const isLoggedIn = getIsLoggedIn();
    triggerLogEvent(analytics, "web_bill_payment_pay_now_redirect", {
      destination: isLoggedIn ? "business_overview" : "signup",
    });
    navigate(isLoggedIn ? "/BusinessOverview" : "/SignUp");
    return;

    if (displayAmount > UCASH_REDIRECT_AMOUNT_THRESHOLD) {
      const isLoggedIn = getIsLoggedIn();
      triggerLogEvent(analytics, "web_bill_payment_high_value_redirect", {
        destination: isLoggedIn ? "ucash" : "signup",
        amount: displayAmount,
      });
      navigate(isLoggedIn ? "/UCash" : "/SignUp");
      return;
    }

    const result = await generateDqr(billingKey, accessToken);
    if (!result.success) {
      showSnackbar({
        message: result.message || I18n.something_went_wrong,
        severity: "error",
      });
      return;
    }
    setIsScanAndPay(true);
  };

  const handleVerifyPayment = async () => {
    verifyStartedAtRef.current = Date.now();
    const result = await verifyPayment(billingKey, accessToken);
    if (result.success) {
      const isFullyPaid = result.paymentReceived >= result.totalPayment;
      triggerLogEvent(
        analytics,
        isFullyPaid
          ? "web_bill_payment_verify_payment_success"
          : "web_bill_payment_verify_payment_partial",
        {
          biller: formValues.billerValue,
          amount_paid: result.paymentReceived,
          total_payment: result.totalPayment,
        },
      );
      const elapsedSec = (
        (Date.now() - verifyStartedAtRef.current) /
        1000
      ).toFixed(1);
      setProcessingTimeSec(`${elapsedSec} sec`);
    } else {
      triggerLogEvent(analytics, "web_bill_payment_verify_payment_failed", {
        biller: formValues.billerValue,
        message: result.message,
      });
      showSnackbar({
        message: result.message || I18n.something_went_wrong,
        severity: "error",
      });
    }
  };

  const resetSuccessState = () => {
    setIsScanAndPay(false);
    setIsPayingRemaining(false);
    setShowReceipt(false);
    setProcessingTimeSec("");
    setIsPrintLoading(false);
    setIsDownloadLoading(false);
    verifyStartedAtRef.current = null;
    resetDqr();
    resetBill();
  };

  const handleOpenFreeAccount = () => {
    const isLoggedIn = getIsLoggedIn();
    triggerLogEvent(analytics, "web_bill_payment_open_free_account_click", {
      destination: isLoggedIn ? "business_overview" : "signup",
    });
    resetSuccessState();
    markOpenFreeAccountFlow();
    navigate(
      isLoggedIn ? "/BusinessOverview" : "/SignUp",
      isLoggedIn ? undefined : { state: { cashbackAmount: 4 } },
    );
  };

  const handleViewReceipt = () => {
    triggerLogEvent(analytics, "web_bill_payment_view_receipt_click");
    setShowReceipt(true);
  };

  const handleReceiptBack = () => {
    triggerLogEvent(analytics, "web_bill_payment_receipt_back");
    setShowReceipt(false);
  };

  const handleContactUs = () => {
    triggerLogEvent(analytics, "web_bill_payment_contact_us_click");
    setIsChatWidgetOpen(true);
    openChatwootWindow();
  };

  const handlePayRemaining = () => {
    triggerLogEvent(analytics, "web_bill_payment_pay_remaining_click", {
      remaining_amount: remainingAmount,
    });
    setRemainingAmountToPay(remainingAmount);
    continueForRemainingPayment();
    setIsPayingRemaining(true);
    setIsScanAndPay(true);
  };

  const isPaidBill = billDetails?.status?.toLowerCase() === "paid";
  const dueDate = billDetails?.dueDate
    ? moment(billDetails.dueDate).format("DD MMM YYYY")
    : "";
  const isPastDeadline = billDetails?.dueDate
    ? moment().isAfter(moment(billDetails.dueDate))
    : false;
  const displayAmount =
    isPastDeadline && billDetails?.amountLate != null
      ? billDetails.amountLate
      : billDetails?.amount;

  const hasExistingPartialPayment = paymentReceived > 0 && remainingAmount > 0;
  const hideScanAndPayToggle =
    isPayingRemaining ||
    displayAmount > HIDE_TOGGLE_AMOUNT_THRESHOLD ||
    (!isPaymentCompleted && hasExistingPartialPayment);
  const isPartialFromDqr =
    !isPaymentCompleted && !isPayingRemaining && hasExistingPartialPayment;

  if (isPaymentCompleted) {
    const consumerName = billDetails?.consumerName || "";
    const consumerNumber = billDetails?.consumerNumber || "";
    const paidAmount = paymentReceived != null ? paymentReceived : displayAmount;
    const isFullyPaid = remainingAmount <= 0;
    const successSubtitle = (
      <>
        {I18n.bill_payment}
        {consumerName ? (
          <>
            {" "}
            {I18n.to?.toLowerCase() || "to"} <strong>{consumerName}</strong>
          </>
        ) : null}
        {billDetails?.billerLogo ? (
          <img
            src={billDetails.billerLogo}
            alt=""
            className={styles.successBillerLogo}
          />
        ) : null}
        {consumerNumber ? <strong>{consumerNumber}</strong> : null}
      </>
    );
    const receiptHeaderText = (
      <>
        {I18n.bill_paid_with} <strong>{I18n.rupin}</strong>
      </>
    );
    const receiptRows = [
      { label: I18n.by || "By", value: I18n.rupin },
      { label: I18n.company, value: getBillerLabel(formValues.billerValue) },
      { label: I18n.date, value: moment().format("D MMMM YYYY, hh:mm A") },
      { label: I18n.fee, value: "Rs. 0 (Free)" },
      {
        label: I18n.total_amount,
        value: `Rs. ${formatNum(paidAmount) || 0}`,
      },
    ];

    const handleDownloadReceipt = () =>
      downloadReceiptImage(receiptRef, {
        setIsDownloadLoading,
        analyticsEvent: "web_bill_payment_download_receipt",
        fileName: "Rupin Bill Payment Receipt",
      });

    const handlePrintReceipt = () =>
      printWalletReceiptPreview(
        business,
        {
          headerText: receiptHeaderText,
          amount: paidAmount,
          customerName: consumerName,
          recipientName: consumerNumber,
          bankInfo: "",
          rows: receiptRows,
        },
        {
          showSnackbar,
          setIsPrintLoading,
          analyticsEvent: "web_bill_payment_print_receipt",
        },
      );

    return (
      <>
        {isChatWidgetOpen ? <ChatwootWidget user={user} /> : null}
        {showReceipt ? (
          <UCashTransactionReceipt
            amount={paidAmount}
            headerText={receiptHeaderText}
            recipientName={consumerNumber}
            customerName={consumerName}
            rows={receiptRows}
            onBack={handleReceiptBack}
            onPrint={handlePrintReceipt}
            isPrintLoading={isPrintLoading}
            onDownload={handleDownloadReceipt}
            isDownloadLoading={isDownloadLoading}
          />
        ) : isFullyPaid ? (
          <UCashTransactionSuccess
            subtitle={successSubtitle}
            amount={paidAmount}
            processingTime={processingTimeSec}
            onViewReceipt={handleViewReceipt}
            primaryBtnTitle={I18n.open_free_account}
            onPrimaryAction={handleOpenFreeAccount}
          />
        ) : (
          <UCashTransactionSuccess
            subtitle={successSubtitle}
            amount={paidAmount}
            remainingAmount={remainingAmount}
            processingTime={processingTimeSec}
            onViewReceipt={handleViewReceipt}
            onClose={handleContactUs}
            secondaryBtnTitle={I18n.contact_us}
            primaryBtnTitle={I18n.pay_remaining}
            onPrimaryAction={handlePayRemaining}
          />
        )}

        <DownloadReceipt
          ref={receiptRef}
          amount={paidAmount}
          headerText={receiptHeaderText}
          customerName={consumerName}
          recipientName={consumerNumber}
          rows={receiptRows}
        />
      </>
    );
  }

  return (
    <>
      <Grid
        container
        className={`${formStyles.root} ${formStyles.SaleExpenseFormWrapper} ${styles.page} WebBillPayment`}
      >
        <Grid item xs={12} md={12}>
          {hasBill ? (
            <TitleAndActionBox
              data={{
                title: isScanAndPay
                  ? isPayingRemaining
                    ? "pay_remaining"
                    : "pay_bill"
                  : "check_pay_bills_with_rupin",
                isTitleCase: false,
                isBack: hasBill,
                onBack: handleBack,
                id: isScanAndPay ? "scan_and_pay" : "confirm_payment",
                className: styles.pageHeader,
              }}
            />
          ) : null}

          <Box
            className={`${styles.shell} ${
              hasBill && !isScanAndPay ? styles.shellWide : isScanAndPay ? styles.scanAndPayContainer : ""
            }`}
          >
            <Box
              className={`${styles.column} ${
                hasBill && !isScanAndPay ? styles.columnWide : ""
              }`}
            >
              {hasBill ? (
                isScanAndPay ? (
                  <ScanAndPay
                    amount={
                      isPayingRemaining
                        ? remainingAmountToPay
                        : isPartialFromDqr
                        ? remainingAmount
                        : displayAmount
                    }
                    amountLabel={
                      isPayingRemaining || isPartialFromDqr
                        ? I18n.remaining_amount
                        : ""
                    }
                    dueDate={dueDate}
                    billerName={getBillerLabel(formValues.billerValue)}
                    billerLogo={billDetails?.billerLogo}
                    consumerName={billDetails?.consumerName}
                    consumerNumber={billDetails?.consumerNumber}
                    iban={dqrDetails?.virtualIban}
                    accountTitle={dqrDetails?.accountTitle}
                    qrValue={dqrDetails?.dqr}
                    onVerifyPayment={handleVerifyPayment}
                    isVerifying={isVerifyingPayment}
                    hideToggle={hideScanAndPayToggle}
                  />
                ) : (
                  <>
                    <Box
                      className={`${billStyles.amountCard} ${styles.amountCard}`}
                    >
                      <ParagraphText
                        className={`fontSize14 newLightColor ${billStyles.amountLabel}`}
                      >
                        {I18n.amount}
                      </ParagraphText>
                      <Box className={billStyles.amountRow}>
                        <ParagraphText
                          className={`fontSize24 fontWeight600 newFontColor ${billStyles.amountValue}`}
                        >
                          Rs. {formatNum(displayAmount || 0)}
                        </ParagraphText>
                      </Box>

                      <Box
                        className={`${billStyles.billDetailRow} ${styles.billDetailRow}`}
                      >
                        {billDetails?.billerLogo ? (
                          <Box className={billStyles.billDetailIconWrapper}>
                            <img
                              src={billDetails.billerLogo}
                              alt=""
                              className={billStyles.billDetailIcon}
                            />
                          </Box>
                        ) : null}
                        <Box className={billStyles.billDetailText}>
                          <ParagraphText className="fontSize14 newFontColor">
                            {billDetails?.consumerNumber}
                          </ParagraphText>
                          {billDetails?.consumerName ? (
                            <ParagraphText className="fontSize14 newLightColor">
                              {billDetails.consumerName}
                            </ParagraphText>
                          ) : null}
                        </Box>
                        {dueDate ? (
                          <ParagraphText
                            className={`fontSize14 newLightColor ${billStyles.dueDate} ${styles.dueDate}`}
                            dir="auto"
                          >
                            {I18n.due} : {dueDate}
                          </ParagraphText>
                        ) : null}
                      </Box>

                      <Box className={styles.metaRow}>
                        {billPdfUrl ? (
                          <ParagraphText
                            className="fontSize14 fontWeight500 barclayBlueColor cursorPointer textDecorationUnerline"
                            onClick={handleDownloadBill}
                          >
                            {I18n.download_pdf}
                          </ParagraphText>
                        ) : (
                          <ParagraphText className="fontSize14 newLightColor">
                            {pdfStatus === PDF_STATUS.FAILED
                              ? I18n.bill_pdf_unavailable
                              : I18n.bill_pdf_processing}
                          </ParagraphText>
                        )}
                        <ParagraphText className="fontSize14 newFontColor">
                          {I18n.status}:{" "}
                          <strong>
                            {isPaidBill ? I18n.paid : I18n.unpaid}
                          </strong>
                        </ParagraphText>
                      </Box>
                    </Box>

                    {!isPaidBill ? (
                      <UBButton
                        id="web_bill_payment_pay_now_btn"
                        title={I18n.pay_bill}
                        iconComponent={<EnterIcon />}
                        iconPlacement="right"
                        className={`buttonPrimary saveSaleButton textUppercase ${styles.payButton}`}
                        onClick={handlePayNow}
                        loading={isGeneratingDqr}
                        disabled={isGeneratingDqr}
                      />
                    ) : null}

                    {billPdfUrl && !hasPdfLoadError ? (
                      <Box className={styles.billImageWrapper}>
                        <PdfPageViewer
                          setHasError={setHasPdfLoadError}
                          setIsLoading={setIsPdfLoading}
                          fileUrl={billPdfUrl}
                        />
                      </Box>
                    ) : null}

                    {/* second Pay button sits under the PDF, so only when the PDF is shown */}
                    {!isPaidBill && billPdfUrl && !hasPdfLoadError && !isPdfLoading ? (
                      <UBButton
                        id="web_bill_payment_pay_now_btn"
                        title={I18n.pay_bill}
                        iconComponent={<EnterIcon />}
                        iconPlacement="right"
                        className={`buttonPrimary saveSaleButton textUppercase ${styles.payButton} marginTop25`}
                        onClick={handlePayNow}
                        loading={isGeneratingDqr}
                        disabled={isGeneratingDqr}
                      />
                    ) : null}
                  </>
                )
              ) : !showLoader ? (
                <BillSearchForm
                  initialReferenceValue={formValues.referenceValue}
                  initialPhoneValue={formValues.phoneValue}
                  initialPhoneError={initialPhoneError}
                  initialBiller={formValues.billerValue}
                  error={error}
                  onSearch={handleBillSearch}
                  onBillerSelect={(biller) =>
                    triggerLogEvent(
                      analytics,
                      "web_bill_payment_biller_select",
                      { biller },
                    )
                  }
                />
              ) : null}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <BusinessLoader loadBusiness={showLoader} />
    </>
  );
};

export default WebBillPayment;
